import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";
import { quickCapture } from "@/lib/api";

/**
 * Quick-capture overlay — the fastest way to add ANYTHING to Mochi.
 *
 * Spec:
 *  - 200ms fade-in + scale animation
 *  - Frosted-glass overlay (rose-tint for feminine, indigo-tint for masculine)
 *  - Mochi bubble: "what's on your mind? 🍡"
 *  - One full-width auto-focused text input
 *  - One "add" button OR Enter to submit
 *  - Escape or backdrop tap to close
 *  - Empty submit → gentle shake + bubble swap to "…you didn't type anything 🍡"
 *
 * Submits to POST /api/capture which classifies via Claude AND creates
 * the item in the right collection, then returns the type + flags so the
 * parent can fire the right confirmation + tab pulse.
 */
export default function QuickCapture({ open, onClose, avatarStyle = "feminine", onCaptured }) {
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [shake, setShake] = useState(false);
    const [shakeMsg, setShakeMsg] = useState(false);
    const inputRef = useRef(null);

    // Reset state + focus the input every time the overlay opens.
    useEffect(() => {
        if (!open) return;
        setText("");
        setShake(false);
        setShakeMsg(false);
        // Tiny delay so the AnimatePresence mount completes before focusing —
        // otherwise the keyboard sometimes doesn't pop on iOS Safari.
        const t = setTimeout(() => {
            try { inputRef.current?.focus(); } catch { /* noop */ }
        }, 120);
        return () => clearTimeout(t);
    }, [open]);

    // Escape closes without saving.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const handleSubmit = async (e) => {
        e?.preventDefault?.();
        if (loading) return;
        const trimmed = text.trim();
        if (!trimmed) {
            setShake(true);
            setShakeMsg(true);
            setTimeout(() => setShake(false), 450);
            // Bubble text reverts to default after a moment.
            setTimeout(() => setShakeMsg(false), 2200);
            return;
        }
        setLoading(true);
        try {
            const res = await quickCapture(trimmed);
            onCaptured?.(res);
            onClose();
        } catch {
            // Network/API failure — keep the overlay open so the user
            // doesn't lose their thought, just nudge them.
            setShake(true);
            setShakeMsg(false);
            setTimeout(() => setShake(false), 450);
        } finally {
            setLoading(false);
        }
    };

    // Tint based on avatar style — rose for feminine, indigo for masculine.
    const masc = avatarStyle === "masculine";
    const cardBg = masc
        ? "bg-gradient-to-br from-indigo-100/85 to-indigo-200/70"
        : "bg-gradient-to-br from-rose-100/85 to-rose-200/70";
    const ringFocus = masc ? "focus:border-indigo-500" : "focus:border-terracotta";
    const btnBg = masc ? "bg-indigo-500 hover:bg-indigo-600" : "bg-terracotta hover:bg-terracotta-hover";

    const bubbleMsg = shakeMsg
        ? "…you didn't type anything 🍡"
        : "what's on your mind? 🍡";

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    // z-index above EVERYTHING (overwhelm button, milestone overlay 50, etc.)
                    className="fixed inset-0 z-[70] backdrop-blur-md bg-warm-900/25 flex items-start justify-center px-4 pt-[15vh] sm:pt-[18vh]"
                    onClick={onClose}
                    data-testid="quick-capture-overlay"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            x: shake ? [0, -8, 8, -6, 6, -3, 0] : 0,
                        }}
                        exit={{ opacity: 0, scale: 0.92 }}
                        transition={{
                            duration: 0.2,
                            x: { duration: 0.45 },
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`w-full max-w-md ${cardBg} rounded-clay shadow-float p-5 sm:p-6`}
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.25rem)" }}
                        data-testid="quick-capture-card"
                    >
                        {/* Mochi bubble at top */}
                        <div className="mb-4">
                            <p
                                className="font-display text-warm-900 text-lg sm:text-xl leading-snug"
                                data-testid="quick-capture-bubble"
                            >
                                {bubbleMsg}
                            </p>
                            <p className="font-body text-[11px] uppercase tracking-widest text-warm-500 mt-1">
                                quick capture · {masc ? "⌘K" : "⌘K"}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <input
                                ref={inputRef}
                                type="text"
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="type anything… mochi will sort it out"
                                disabled={loading}
                                maxLength={2000}
                                aria-label="What's on your mind?"
                                className={`w-full bg-cream/90 rounded-2xl px-4 py-3.5 font-body text-warm-900 outline-none border-2 border-transparent transition-all ${ringFocus}`}
                                data-testid="quick-capture-input"
                            />
                            <div className="flex items-center justify-between mt-4">
                                <p className="font-body text-[11px] text-warm-500">
                                    {loading ? "mochi is sorting…" : "press Enter to add · Esc to close"}
                                </p>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className={`inline-flex items-center gap-1.5 font-display text-sm uppercase tracking-widest px-5 py-2.5 rounded-full text-cream transition-all active:scale-95 disabled:opacity-60 ${btnBg}`}
                                    data-testid="quick-capture-submit"
                                >
                                    <Sparkles size={14} />
                                    add
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
